/**
 * `SaleSlice` — the shared contract. A §5.2 split: this file owns the common state fields and
 * the four reducers whose bodies are identical, and each platform file makes its own
 * `createSlice`.
 *
 * Measured: 5 of 6 state fields shared (web adds `saleValidated`, mobile adds `itemQtyMap`,
 * `modesSelectionArr`, `selectedMode`); 4 of the reducers shareable, 2 divergent
 * (`saveSaleData`, `resetSaleStore`), 2 web-only, 4 mobile-only.
 *
 * **The two divergent bodies, side by side** — this table lives HERE and not in either platform
 * file, because a platform file's comments land in that platform's bundle and a comment naming
 * the other platform's identifiers reads as a resolution leak to a symbol-level grep
 * (ledger hazard 16):
 *
 * | | web | mobile |
 * |---|---|---|
 * | `saveSaleData` | destructures `saleValidated` off the payload into its own field | stores the payload whole, mutating it in place |
 * | `saveSaleData` | stamps `isHoldOrder: true` on a Held -> Open conversion | converts Held -> Open with no stamp |
 * | `resetSaleStore` | clears the four shared fields | clears those PLUS `itemQtyMap` |
 * | logout | `userLogout` / `supportLogout` reset the slice | no `extraReducers` at all |
 *
 * 🔴 **`saleData` is deliberately NOT in `sharedInitialState`.** Its default is
 * `getBasicSaleData()`, which is platform-divergent (web starts `createTickets:false` and an
 * empty `createdAt`; mobile `true` and a timestamp), so `sharedInitialState` is typed
 * `Omit<..., "saleData">` to force each platform file to supply its own — hazard 7's shape,
 * third use on this branch.
 *
 * 🔴 **The two apps' `initialState` are NOT both complete, and that is preserved.** Mobile's
 * `Props` requires `modesSelectionArr` and `selectedMode` but its `initialState` supplies
 * neither — a TS2739 that is one of the 287 rows in `docs/mobile-tsc-baseline.txt` (ledger
 * hazard 3). `index.native.ts` reproduces the gap verbatim; do not "fix" it here.
 *
 * ⚠️ Every reducer's action is `PayloadAction<any>`, never `any` — bare `any` makes RTK infer
 * the NO-PAYLOAD overload and every consumer's `saveGuestInfo(x)` stops compiling. This is the
 * sharpest trap on the branch; `--verify` fails on it (selftest case 13).
 */
import type { PayloadAction } from "@reduxjs/toolkit";
import type { SharedSaleDataType } from "../../../models/sale/shared";

export interface SharedSaleState {
  saleData?: SharedSaleDataType;
  guestInfo: any;
  selectedChannelData?: any;
  isHoldSaleCall: boolean;
  updateSelectedChannelDataOffline: boolean;
}

/** `saleData` omitted on purpose — see the header. */
export const sharedInitialState: Omit<SharedSaleState, "saleData"> = {
  guestInfo: {},
  selectedChannelData: {},
  isHoldSaleCall: true,
  updateSelectedChannelDataOffline: false,
};

export const sharedReducers = {
  saveSelectedChannel: (state: any, action: PayloadAction<any>) => {
    state.selectedChannelData = action.payload;
    state.updateSelectedChannelDataOffline = false;
  },
  saveSelectedChannelOffline: (state: any, action: PayloadAction<any>) => {
    state.selectedChannelData = action.payload;
    state.updateSelectedChannelDataOffline = true;
  },
  saveGuestInfo: (state: any, action: PayloadAction<any>) => {
    state.guestInfo = action.payload;
  },
  // `action` is unused on BOTH platforms but declared on both — the arity is load-bearing for
  // the generated creator's type signature (hazard 14), so it stays.
  holdSaleData: (state: any, action: PayloadAction<any>) => {
    state.isHoldSaleCall = true;
  },
};
